import { Fragment, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import SectionHeading from "./SectionHeading";
import { ChevronDown } from "./icons";

interface RequestItem {
  name: string;
  quantity: number;
  price?: number;
}

interface RequestSummary {
  id: number;
  items: string;
  status: "new" | "in_review" | "offer_sent" | "cancelled";
  has_offer: number;
  created_at: string;
}

function requestStatusLabel(
  status: RequestSummary["status"],
  t: (
    key:
      | "requestStatusNew"
      | "requestStatusInReview"
      | "requestStatusOfferSent"
      | "requestStatusCancelled"
  ) => string
): string {
  if (status === "in_review") return t("requestStatusInReview");
  if (status === "offer_sent") return t("requestStatusOfferSent");
  if (status === "cancelled") return t("requestStatusCancelled");
  return t("requestStatusNew");
}

const STATUS_BADGE_CLASS: Record<RequestSummary["status"], string> = {
  new: "bg-steel-100 text-steel-600",
  in_review: "bg-amber-100 text-amber-700",
  offer_sent: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-steel-100 text-steel-400",
};

const CANCELLABLE_STATUSES: RequestSummary["status"][] = ["new", "in_review"];

function formatRequestNumber(id: number): string {
  return `#${String(id).padStart(5, "0")}`;
}

function parseItems(items: string): RequestItem[] {
  try {
    const parsed = JSON.parse(items) as unknown;
    return Array.isArray(parsed) ? (parsed as RequestItem[]) : [];
  } catch {
    return [];
  }
}

function requestTotalLabel(
  items: RequestItem[],
  t: (key: "pricePrefixFrom" | "priceOnRequest") => string
): string {
  const knownTotal = items.reduce((sum, item) => sum + (item.price ?? 0) * item.quantity, 0);
  const hasUnknownPrice = items.some((item) => item.price === undefined);
  if (hasUnknownPrice) {
    return knownTotal > 0
      ? `${t("pricePrefixFrom")}${knownTotal.toLocaleString("ru-RU")} ₸`
      : t("priceOnRequest");
  }
  return `${knownTotal.toLocaleString("ru-RU")} ₸`;
}

// SQLite's datetime('now') returns UTC with no timezone marker in a
// space-separated (non-ISO) format — new Date() would otherwise parse it as
// local time, which is wrong for this Kazakhstan-timezone audience.
function formatRequestDate(createdAt: string): string {
  return new Date(`${createdAt.replace(" ", "T")}Z`).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AccountPage() {
  const { t } = useLanguage();
  const { user, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<RequestSummary[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [cancelErrorId, setCancelErrorId] = useState<number | null>(null);

  useEffect(() => {
    const loadRequests = () => {
      fetch("/api/requests")
        .then((res) => (res.ok ? res.json() : []))
        .then(setRequests)
        .catch(() => {});
    };

    loadRequests();

    // Status/offer changes come from the admin panel, on a different device
    // and session — polling plus a refetch on tab focus is what "shows up
    // without the customer manually reloading" means here, absent any
    // push/websocket infrastructure in this app.
    const interval = window.setInterval(loadRequests, 15000);
    const onVisible = () => {
      if (document.visibilityState === "visible") loadRequests();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", loadRequests);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", loadRequests);
    };
  }, []);

  const handleCancel = async (id: number) => {
    if (!window.confirm(t("accountRequestCancelConfirm"))) return;
    setCancelErrorId(null);
    setCancellingId(id);
    try {
      const res = await fetch(`/api/requests/${id}/cancel`, { method: "PUT" });
      if (!res.ok) {
        setCancelErrorId(id);
        return;
      }
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: "cancelled" } : r)));
    } catch {
      setCancelErrorId(id);
    } finally {
      setCancellingId(null);
    }
  };

  if (!isLoading && !user) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  return (
    <section className="bg-white pb-24 pt-40 md:pb-32">
      <div className="container-industrial">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <SectionHeading title={t("accountPageTitle")} variant="sans" size="md" />
          <div className="flex items-center gap-4">
            <span className="text-sm text-steel-500">
              {t("accountLoggedInAs")} {user?.name}
            </span>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="btn-dark-outline shrink-0"
            >
              {t("logout")}
            </button>
          </div>
        </div>

        <div className="mt-12">
          <SectionHeading title={t("accountRequestsTitle")} variant="sans" size="sm" />
          {requests.length > 0 ? (
            <div className="mt-6 overflow-x-auto border border-steel-200">
              <table className="w-full min-w-[820px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-steel-200 bg-steel-50">
                    <th className="px-4 py-3 font-semibold text-steel-500">
                      {t("accountRequestsColumnRequest")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-steel-500">
                      {t("accountRequestsColumnDate")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-steel-500">
                      {t("accountRequestsColumnAmount")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-steel-500">
                      {t("accountRequestsColumnStatus")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-steel-500">
                      {t("accountRequestsColumnDocuments")}
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-steel-500">
                      {t("accountRequestsColumnDetails")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => {
                    const items = parseItems(request.items);
                    const isExpanded = expandedId === request.id;
                    return (
                      <Fragment key={request.id}>
                        <tr className="border-b border-steel-100 last:border-b-0">
                          <td className="px-4 py-3 font-semibold text-ink">
                            {formatRequestNumber(request.id)}
                          </td>
                          <td className="px-4 py-3 text-steel-600">
                            {formatRequestDate(request.created_at)}
                          </td>
                          <td className="px-4 py-3 text-ink">{requestTotalLabel(items, t)}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE_CLASS[request.status]}`}
                            >
                              {requestStatusLabel(request.status, t)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {request.has_offer === 1 ? (
                              <a
                                href={`/api/requests/${request.id}/offer`}
                                className="font-semibold text-accent hover:text-accent-hover"
                              >
                                {t("accountRequestDownload")}
                              </a>
                            ) : (
                              <span className="text-steel-400">
                                {t("accountRequestDocumentsPending")}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              aria-expanded={isExpanded}
                              aria-label={t("accountRequestsColumnDetails")}
                              onClick={() => setExpandedId(isExpanded ? null : request.id)}
                              className="ml-auto flex h-8 w-8 items-center justify-center text-steel-500 transition-colors duration-150 ease-engineered hover:text-accent"
                            >
                              <ChevronDown
                                size={18}
                                className={`transition-transform duration-200 ease-engineered ${isExpanded ? "rotate-180" : ""}`}
                              />
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="border-b border-steel-100 bg-steel-50">
                            {/* A <tr>/<td> can't smoothly animate open like the
                                grid-rows folds used elsewhere — table row
                                sizing doesn't support that — so the row still
                                mounts instantly, but its content fades and
                                settles in rather than appearing as a flat
                                pop-in. */}
                            <td colSpan={6} className="animate-captionIn px-4 py-4">
                              <p className="mb-2 text-xs uppercase tracking-wide2 text-steel-500">
                                {t("accountRequestItemsCount")} {items.length}
                              </p>
                              <ul className="flex flex-col gap-1.5">
                                {items.map((item, i) => (
                                  <li
                                    key={i}
                                    className="flex items-center justify-between gap-4 text-sm text-steel-600"
                                  >
                                    <span>
                                      {item.name} × {item.quantity}
                                    </span>
                                    {item.price !== undefined && (
                                      <span className="shrink-0 text-ink">
                                        {(item.price * item.quantity).toLocaleString("ru-RU")} ₸
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                              {CANCELLABLE_STATUSES.includes(request.status) && (
                                <div className="mt-4 border-t border-steel-200 pt-4">
                                  <button
                                    type="button"
                                    disabled={cancellingId === request.id}
                                    onClick={() => handleCancel(request.id)}
                                    className="text-sm font-semibold text-accent hover:text-accent-hover disabled:opacity-60"
                                  >
                                    {cancellingId === request.id ? "…" : t("accountRequestCancel")}
                                  </button>
                                  {cancelErrorId === request.id && (
                                    <p className="mt-1.5 text-xs text-accent">
                                      {t("accountRequestCancelError")}
                                    </p>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-6 border border-steel-200 p-8 text-center">
              <p className="mx-auto max-w-md text-sm leading-relaxed text-steel-600">
                {t("accountRequestsEmpty")}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
